import { convexQuery } from "@convex-dev/react-query";
import { api } from "@convex/_generated/api";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAction, useConvexAuth } from "convex/react";
import { LogOut } from "lucide-react";
import { useEffect, useState } from "react";

import {
  useActiveOrganization,
  useAuthActions,
  useCurrentUser,
} from "~/integrations/vortex-auth/hooks";
import { logger } from "~/lib/logger";
import { optionalSearchString } from "~/lib/parse-enum-value";

// Avoids TS2589 (type instantiation is excessively deep) from api.d.ts chain
const createAccountRef = api.stripeAccounts.createAccount;

const getBusinessInformationRef = api.organizations.getBusinessInformation;

import { Button } from "../components/ui/button";
import { Spinner } from "../components/ui/spinner";

export const Route = createFileRoute("/onboarding")({
  component: OnboardingPage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      setup: optionalSearchString(search.setup),
    };
  },
});

function OnboardingPage() {
  const navigate = useNavigate();
  const { organization } = useActiveOrganization();
  const orgId = organization?.id ?? null;
  const { user } = useCurrentUser();
  const { signOut } = useAuthActions();
  const { isAuthenticated, isLoading: isConvexLoading } = useConvexAuth();
  const isAuthLoaded = !isConvexLoading;
  const isSignedIn = isAuthenticated;
  const { setup: setupSuccess } = Route.useSearch();

  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [hasNavigated, setHasNavigated] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAccount = useAction(createAccountRef);

  const handleSignOut = async () => {
    await signOut();
    await navigate({ to: "/sign-in" });
  };

  // Load existing organization data to check if already has Stripe account
  const { data: existingOrgData, isLoading: isOrgLoading } = useQuery({
    ...convexQuery(
      getBusinessInformationRef,
      isAuthenticated && orgId ? {} : "skip"
    ),
  });

  // Redirect to sign-in if not authenticated
  useEffect(() => {
    if (isAuthLoaded && !isSignedIn) {
      void navigate({ to: "/sign-in" });
    }
  }, [isAuthLoaded, isSignedIn, navigate]);

  // Active organization is resolved server-side via vortex-auth; no client-side
  // org activation is needed.

  // Handle return from Stripe payment setup redirect (legacy)
  useEffect(() => {
    if (setupSuccess === "success" && !hasNavigated) {
      setHasNavigated(true);
      void navigate({ to: "/" });
    }
  }, [setupSuccess, navigate, hasNavigated]);

  // If already has Stripe account, redirect to portal
  useEffect(() => {
    if (existingOrgData?.stripeAccountId && !hasNavigated) {
      setHasNavigated(true);
      void navigate({ to: "/" });
    }
  }, [existingOrgData, navigate, hasNavigated]);

  // Auto-create Stripe account when org data is loaded and no account exists
  useEffect(() => {
    // Guard: Check all preconditions in one place
    const canCreateAccount =
      isAuthenticated &&
      orgId &&
      user?.primaryEmailAddress?.emailAddress &&
      !existingOrgData?.stripeAccountId &&
      !isOrgLoading &&
      !isCreatingAccount &&
      !hasNavigated;

    if (!canCreateAccount) {
      return;
    }

    const createStripeAccount = async () => {
      try {
        setIsCreatingAccount(true);
        setError(null);

        await createAccount({
          organizationId: orgId,
          email: user.primaryEmailAddress?.emailAddress ?? "",
          businessName: user.fullName ?? "New Business",
        });

        // Account created - navigate to portal
        setHasNavigated(true);
        void navigate({ to: "/" });
      } catch (err) {
        logger.exception("stripe_account_creation_failed", err, { orgId });
        setError(
          err instanceof Error ? err.message : "Failed to create account"
        );
        setIsCreatingAccount(false);
      }
    };

    void createStripeAccount();
  }, [
    orgId,
    user,
    existingOrgData,
    isOrgLoading,
    isCreatingAccount,
    isAuthenticated,
    createAccount,
    navigate,
    hasNavigated,
  ]);

  // Show loading state while checking auth and org status
  const isLoading =
    !isAuthLoaded ||
    isConvexLoading ||
    Boolean(orgId && isOrgLoading) ||
    isCreatingAccount;

  if (isLoading) {
    return (
      <div className="bg-background flex h-dvh flex-col">
        <header className="flex items-center justify-between px-8 py-6">
          <img
            alt="Plasma"
            height={48}
            src="/images/plasma-favicon.png"
            width={48}
          />
          <Button
            onClick={handleSignOut}
            size="sm"
            type="button"
            variant="ghost"
          >
            <LogOut className="mr-2 size-4" />
            Sign out
          </Button>
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <Spinner className="size-8" />
            <p className="text-muted-foreground">
              {isCreatingAccount ? "Setting up your account..." : "Loading..."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-background flex h-dvh flex-col">
        <header className="flex items-center justify-between px-8 py-6">
          <img
            alt="Plasma"
            height={48}
            src="/images/plasma-favicon.png"
            width={48}
          />
          <Button
            onClick={handleSignOut}
            size="sm"
            type="button"
            variant="ghost"
          >
            <LogOut className="mr-2 size-4" />
            Sign out
          </Button>
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="flex max-w-md flex-col gap-4 text-center">
            <div className="bg-destructive/20 mx-auto size-12 rounded-full p-3">
              <svg
                aria-label="Error icon"
                className="text-destructive size-full"
                fill="none"
                role="img"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  d="M6 18L18 6M6 6l12 12"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
            <h1 className="font-heading text-foreground text-2xl text-balance">
              Something went wrong
            </h1>
            <p className="text-muted-foreground">{error}</p>
            <p className="text-muted-foreground text-sm">
              Please contact support at{" "}
              <a
                className="text-foreground font-medium underline"
                href="mailto:hi@vortexnyc.com"
              >
                hi@vortexnyc.com
              </a>
            </p>
            <Button
              onClick={() => {
                setError(null);
                setIsCreatingAccount(false);
              }}
              type="button"
              variant="outline"
            >
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If no organization, show create workspace message
  if (!orgId) {
    return (
      <div className="bg-background flex h-dvh flex-col">
        <header className="flex items-center justify-between px-8 py-6">
          <img
            alt="Plasma"
            height={48}
            src="/images/plasma-favicon.png"
            width={48}
          />
          <Button
            onClick={handleSignOut}
            size="sm"
            type="button"
            variant="ghost"
          >
            <LogOut className="mr-2 size-4" />
            Sign out
          </Button>
        </header>
        <div className="flex flex-1 items-center justify-center">
          <div className="flex max-w-md flex-col gap-4 text-center">
            <h1 className="font-heading text-foreground text-2xl text-balance">
              Create a workspace
            </h1>
            <p className="text-muted-foreground">
              You need to create a workspace before continuing.
            </p>
            <Button
              onClick={() => navigate({ to: "/create-organization" })}
              type="button"
            >
              Create workspace
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Default: should not reach here, but show loading just in case
  return (
    <div className="bg-background flex h-dvh flex-col">
      <header className="flex items-center justify-between px-8 py-6">
        <img
          alt="Plasma"
          height={48}
          src="/images/plasma-favicon.png"
          width={48}
        />
        <Button onClick={handleSignOut} size="sm" type="button" variant="ghost">
          <LogOut className="mr-2 size-4" />
          Sign out
        </Button>
      </header>
      <div className="flex flex-1 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Spinner className="size-8" />
          <p className="text-muted-foreground">Setting up your account...</p>
        </div>
      </div>
    </div>
  );
}
